/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { Typography, Box, Skeleton } from '@mui/material';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import { useOutletContext } from 'react-router-dom';
import {
  EducationalBackgroundTab,
  CivilServiceTab,
  EmployeeHistoryTab,
  EmployeeTrainingsTab,
  FamilyBackgroundTab,
  PersonalInfoTab,
  VoluntaryWorksTab,
  WorkExperienceTab,
  AddressesTab,
} from '../index';

export const InspectEmpDetails = forwardRef((props, ref) => {
  const [empId] = useOutletContext();
  const [showSkeleton, setShowSkeleton] = useState(true);

  // DATA STATES
  const [emp, setEmp] = useState({});
  const [empAddress, setEmpAddress] = useState([]);
  const [empWorkExp, setEmpWorkExp] = useState([]);
  const [empVolWork, setVolWork] = useState([]);
  const [emptrn, setEmpTrn] = useState([]);
  const [empRelations, setEmpRelations] = useState([]);
  const [empEducation, setEmpEducation] = useState([]);
  const [empCivService, setEmpCivService] = useState([]);
  const [empHistory, setEmpHistory] = useState({ empid: empId });

  useImperativeHandle(ref, () => ({
    getAllData: () => ({
      emp,
      empAddress,
      empWorkExp,
      empVolWork,
      emptrn,
      empRelations,
      empEducation,
      empCivService,
      empHistory,
    }),
  }));

  // Helper to fetch data from endpoint
  const fetchData = async (endpoint, setter) => {
    try {
      const res = await fetch(apiUrl(endpoint), {
        method: 'GET',
        credentials: 'include',
      });
      const result = await res.json();
      if (result.success) setter(result.data);
    } catch (err) {
      console.error(err);
    }
  };

  // For Inspect
  useEffect(() => {
    if (props.empid === undefined) return;

    const loadAllData = async () => {
      setShowSkeleton(true);

      await Promise.all([
        fetchData(`/employee/${props.empid}`, setEmp),
        fetchData(`/eis/empaddress/${props.empid}`, setEmpAddress),
        fetchData(`/eis/empworkexp/${props.empid}`, setEmpWorkExp),
        fetchData(`/eis/empcivicwork/${props.empid}`, setVolWork),
        fetchData(`/eis/emptraining/${props.empid}`, setEmpTrn),
        fetchData(`/eis/emprelations/${props.empid}`, setEmpRelations),
        fetchData(`/eis/empeducation/${props.empid}`, setEmpEducation),
        fetchData(`/eis/empcivservice/${props.empid}`, setEmpCivService),
        fetchData(`/eis/emphistory/${props.empid}`, setEmpHistory),
      ]);

      setShowSkeleton(false); // hide skeleton when done
    };

    loadAllData();
  }, [props.empid]);

  // For Edit Request
  useEffect(() => {
    if (!props.editRequest) return;

    setShowSkeleton(true);

    const proposeddata = props.editRequest.proposeddata;
    const empdata = {
      ...proposeddata.basicinfo,
      ...proposeddata.personalinfo,
    };

    setEmp(empdata);
    setEmpAddress(proposeddata.empaddress);
    setEmpRelations(proposeddata.emprelations);
    setEmpEducation(proposeddata.empeducation);
    setEmpCivService(proposeddata.empcivservice);
    setEmpWorkExp(proposeddata.empworkexp);
    setVolWork(proposeddata.empcivicwork);
    setEmpTrn(proposeddata.emptraining);
    setEmpHistory(proposeddata.emphistory);

    setShowSkeleton(false); // hide skeleton when done
  }, [props.editRequest]);

  const SkeletonLoader = () => (
    <Box sx={{ flex: 1, mt: '30px' }}>
      <Skeleton height={60} />
      <Skeleton height={60} />
      <Skeleton height={70} />
      <Skeleton height={80} />
    </Box>
  );

  if (showSkeleton) return <SkeletonLoader />;

  return (
    <>
      {props.currentTab === 'personalInfo' && (
        <PersonalInfoTab
          viewOnly={props.viewOnly}
          personalInfo={emp}
          setPersonalInfo={setEmp}
        />
      )}

      {props.currentTab === 'addresses' && (
        <AddressesTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empAddress={empAddress}
          setEmpAddress={setEmpAddress}
        />
      )}

      {props.currentTab === 'familyBackground' && (
        <FamilyBackgroundTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empRelations={empRelations}
          setEmpRelations={setEmpRelations}
        />
      )}

      {props.currentTab === 'educBackground' && (
        <EducationalBackgroundTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empEducation={empEducation}
          setEmpEducation={setEmpEducation}
        />
      )}

      {props.currentTab === 'civService' && (
        <CivilServiceTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empCivService={empCivService}
          setEmpCivService={setEmpCivService}
        />
      )}

      {props.currentTab === 'workExp' && (
        <WorkExperienceTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empWorkExp={empWorkExp}
          setEmpWorkExp={setEmpWorkExp}
        />
      )}

      {props.currentTab === 'volWork' && (
        <VoluntaryWorksTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          empVolWork={empVolWork}
          setVolWork={setVolWork}
        />
      )}

      {props.currentTab === 'trainingProgs' && (
        <EmployeeTrainingsTab
          viewOnly={props.viewOnly}
          empid={props.empid}
          emptrn={emptrn}
          setEmpTrn={setEmpTrn}
        />
      )}

      {props.currentTab === 'history' && (
        <EmployeeHistoryTab
          viewOnly={props.viewOnly}
          empHistory={empHistory}
          setEmpHistory={setEmpHistory}
        />
      )}
    </>
  );
});
